import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = '/api';

// Función auxiliar para formatear fechas correctamente
function formatDateCorrectly(dateString) {
  if (!dateString) return 'N/A';
  
  // Si la fecha ya tiene formato ISO con T (viene del servidor)
  if (typeof dateString === 'string' && dateString.includes('T')) {
    // Extraer solo la parte YYYY-MM-DD y formatearla manualmente
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  }
  
  // Si es una fecha simple YYYY-MM-DD
  if (typeof dateString === 'string' && dateString.includes('-') && dateString.length === 10) {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }
  
  // Fallback: intentar como mejor podamos
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
  } catch (e) {
    console.error("Error formateando fecha:", e);
  }
  
  return 'N/A';
}

export const useAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [showImportAccountsModal, setShowImportAccountsModal] = useState(false);
  const [showAssignProfileForm, setShowAssignProfileForm] = useState(false);
  const [showEditProfileForm, setShowEditProfileForm] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [newAccount, setNewAccount] = useState({ 
    serviceId: 1, 
    username: '', 
    password: '', 
    observation: '',
    profiles: [{ name: '', status: 'libre' }] 
  });
  const [profileAssignment, setProfileAssignment] = useState({ 
    accountId: null,
    profileId: null,
    profileName: '',
    clientId: '', 
    pin: '', 
    expiryDate: new Date(Date.now() + 30*24*60*60*1000).toISOString().slice(0, 10),
    serviceName: '',
    servicePrice: 0
  });
  const [editingProfile, setEditingProfile] = useState({
    id: null,
    accountId: null,
    name: '',
    status: '',
    clientId: '',
    pin: '',
    expiryDate: ''
  });
  const [currentAccountPage, setCurrentAccountPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientResults, setShowClientResults] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [tooltipInfo, setTooltipInfo] = useState({ show: false, content: null, position: { x: 0, y: 0 } });
  const [hiddenAccounts, setHiddenAccounts] = useState([]);
  const [showHiddenAccounts, setShowHiddenAccounts] = useState(false);
  const [serviceFilter, setServiceFilter] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10
  });
  
  const { csrfToken, refreshCsrfToken } = useAuth();

  // Cargar cuentas ocultas desde localStorage al iniciar
  useEffect(() => {
    const savedHiddenAccounts = localStorage.getItem('hiddenAccounts');
    if (savedHiddenAccounts) {
      setHiddenAccounts(JSON.parse(savedHiddenAccounts));
    }
  }, []);

  const ensureFreshCsrfToken = async () => {
    try {
      const result = await refreshCsrfToken();
      if (!result.success) {
        console.error('Error al refrescar el token CSRF');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error al refrescar el token CSRF:', error);
      return false;
    }
  };

  const fetchAccounts = async (page = currentAccountPage, limit = itemsPerPage) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      // Aplicar filtros en la petición
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (serviceFilter) {
        params.append('serviceId', serviceFilter);
      }
      // Agregar el filtro de estado
      if (expiryFilter && expiryFilter !== 'all') {
        params.append('expiryFilter', expiryFilter);
      }
      
      const response = await axios.get(`${API_URL}/accounts?${params.toString()}`);
      
      // Manejar la respuesta paginada
      if (response.data.accounts && response.data.pagination) {
        setAccounts(response.data.accounts);
        setPaginationInfo(response.data.pagination);
      } else {
        // Fallback para respuesta sin paginación
        setAccounts(response.data);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar las cuentas. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAccount = async () => {
    try {
      setLoading(true);
      
      if (!await ensureFreshCsrfToken()) {
        setError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      const normalizedProfiles = newAccount.profiles.map(profile => {
        if (profile.status === 'ocupado' && !profile.expiryDate && !profile.expiry_date) {
          const defaultExpiryDate = new Date(Date.now() + 30*24*60*60*1000).toISOString().slice(0, 10);
          return {
            ...profile,
            expiryDate: defaultExpiryDate,
            expiry_date: defaultExpiryDate
          };
        }
        return profile;
      });
      
      const accountData = {
        serviceId: parseInt(newAccount.serviceId),
        username: newAccount.username,
        password: newAccount.password,
        observation: newAccount.observation,
        profiles: normalizedProfiles.map(profile => ({
          name: profile.name,
          status: profile.status,
          clientId: profile.clientId ? parseInt(profile.clientId) : null,
          pin: profile.pin || null,
          expiryDate: profile.expiryDate || profile.expiry_date || null,
          expiry_date: profile.expiry_date || profile.expiryDate || null
        }))
      };
  
      if (newAccount.id) {
        await axios.put(`${API_URL}/accounts/${newAccount.id}`, accountData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      } else {
        await axios.post(`${API_URL}/accounts`, accountData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      }
  
      await fetchAccounts();
      setNewAccount({ serviceId: '', username: '', password: '', observation: '', profiles: [{ name: '', status: 'libre' }] });
      setShowAccountForm(false);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al guardar la cuenta. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta cuenta?')) {
      try {
        setLoading(true);
        
        if (!await ensureFreshCsrfToken()) {
          setError('Error al obtener un token CSRF válido. Intente recargar la página.');
          setLoading(false);
          return;
        }
        
        await axios.delete(`${API_URL}/accounts/${accountId}`, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });

        await fetchAccounts();
        setError(null);
      } catch (err) {
        console.error('Error:', err);
        setError('Error al eliminar la cuenta. Por favor, intente de nuevo.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditAccount = (account) => {
    setNewAccount({
      id: account.id,
      serviceId: account.service_id || account.serviceId,
      username: account.username,
      password: account.password,
      observation: account.observation || '',
      profiles: account.profiles.map(profile => ({
        id: profile.id,
        name: profile.name,
        status: profile.status,
        clientId: profile.client_id || profile.clientId,
        pin: profile.pin || '',
        expiryDate: profile.expiry_date || profile.expiryDate || '',
        expiry_date: profile.expiry_date || profile.expiryDate || ''
      }))
    });
    setShowAccountForm(true);
  };

  const handleAssignProfile = (account, profile, services) => {
    const service = services.find(s => s.id === parseInt(account.service_id || account.serviceId));
    
    const defaultExpiryDate = new Date(Date.now() + 30*24*60*60*1000).toISOString().slice(0, 10);
    
    setProfileAssignment({
      accountId: account.id,
      profileId: profile.id,
      profileName: profile.name,
      clientId: '',
      pin: '',
      expiryDate: defaultExpiryDate,
      serviceName: service ? service.name : '',
      servicePrice: service ? parseFloat(service.price) : 0
    });
    
    setClientSearchQuery('');
    setShowAssignProfileForm(true);
  };

  const handleEditProfile = (account, profile) => {
    if (profile.status === 'ocupado') {
      let expiryDate = profile.expiryDate || profile.expiry_date || new Date().toISOString().slice(0, 10);
      
      // Convertir la fecha al formato YYYY-MM-DD requerido por el input type="date"
      if (expiryDate && expiryDate.includes('T')) {
        // Si la fecha tiene formato ISO con T (viene del servidor)
        expiryDate = expiryDate.split('T')[0];
      }
      
      setEditingProfile({
        id: profile.id,
        accountId: account.id,
        name: profile.name,
        status: profile.status,
        clientId: profile.client_id || profile.clientId,
        pin: profile.pin,
        expiryDate: expiryDate,
        expiry_date: expiryDate
      });
      
      setShowEditProfileForm(true);
    } else {
      alert(`Este perfil está ${profile.status}. Solo se pueden editar perfiles ocupados.`);
    }
  };

  const handleSaveProfileAssignment = async (services, clients, movements, getServiceTemplate) => {
    try {
      setLoading(true);
      
      if (!await ensureFreshCsrfToken()) {
        setError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      const accountToUpdate = accounts.find(a => a.id === profileAssignment.accountId);
      
      if (!accountToUpdate) {
        throw new Error('Cuenta no encontrada');
      }
      
      const updatedProfiles = accountToUpdate.profiles.map(profile => {
        if (profile.id === profileAssignment.profileId) {
          return {
            ...profile,
            status: 'ocupado',
            client_id: parseInt(profileAssignment.clientId),
            clientId: parseInt(profileAssignment.clientId),
            pin: profileAssignment.pin || null,
            expiry_date: profileAssignment.expiryDate,
            expiryDate: profileAssignment.expiryDate
          };
        }
        return profile;
      });
      
      const accountData = {
        serviceId: parseInt(accountToUpdate.service_id || accountToUpdate.serviceId),
        username: accountToUpdate.username,
        password: accountToUpdate.password,
        observation: accountToUpdate.observation || '',
        profiles: updatedProfiles.map(profile => ({
          name: profile.name,
          status: profile.status,
          clientId: profile.client_id || profile.clientId,
          pin: profile.pin,
          expiry_date: profile.expiry_date || profile.expiryDate,
          expiryDate: profile.expiryDate || profile.expiry_date
        }))
      };    
      
      await axios.put(`${API_URL}/accounts/${profileAssignment.accountId}`, accountData, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      
      const movementData = {
        date: new Date().toISOString().slice(0, 10),
        type: 'venta',
        amount: profileAssignment.servicePrice,
        clientId: parseInt(profileAssignment.clientId),
        serviceId: parseInt(accountToUpdate.service_id || accountToUpdate.serviceId),
        description: `Venta de perfil "${profileAssignment.profileName}" del servicio "${profileAssignment.serviceName}". Vence: ${formatDateCorrectly(profileAssignment.expiryDate)}`
      };
      
      await axios.post(`${API_URL}/movements`, movementData, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      
      // Copiar información al portapapeles
      const account = accounts.find(a => a.id === profileAssignment.accountId);
      const service = services.find(s => s.id === parseInt(account.service_id || account.serviceId));
      const replacements = {
        nombre_servicio: service.name || 'N/A',
        correo: account.username || 'N/A',
        contraseña: account.password || 'N/A',
        perfil: profileAssignment.profileName || 'N/A',
        pin: profileAssignment.pin || 'N/A',
        fecha_vencimiento: formatDateCorrectly(profileAssignment.expiryDate)
      };
      
      const templateText = getServiceTemplate(service.id);
      const infoToCopy = safeReplaceTemplateVars(templateText, replacements);

      await navigator.clipboard.writeText(infoToCopy);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);

      await fetchAccounts();
      setShowAssignProfileForm(false);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al asignar el perfil. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEditedProfile = async () => {
    try {
      setLoading(true);
      
      if (!await ensureFreshCsrfToken()) {
        setError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      if (editingProfile.status === 'ocupado' && !editingProfile.expiryDate && !editingProfile.expiry_date) {
        const defaultExpiryDate = new Date(Date.now() + 30*24*60*60*1000).toISOString().slice(0, 10);
        editingProfile.expiryDate = defaultExpiryDate;
        editingProfile.expiry_date = defaultExpiryDate;
      }
      
      const accountToUpdate = accounts.find(a => a.id === editingProfile.accountId);
      
      if (!accountToUpdate) {
        throw new Error('Cuenta no encontrada');
      }
      
      const updatedProfiles = accountToUpdate.profiles.map(profile => {
        if (profile.id === editingProfile.id) {
          const expiryDate = editingProfile.expiryDate || editingProfile.expiry_date;
          
          return {
            ...profile,
            name: editingProfile.name,
            status: editingProfile.status,
            client_id: parseInt(editingProfile.clientId),
            clientId: parseInt(editingProfile.clientId),
            pin: editingProfile.pin || null,
            expiry_date: expiryDate,
            expiryDate: expiryDate
          };
        }
        return profile;
      });
      
      const accountData = {
        serviceId: parseInt(accountToUpdate.service_id || accountToUpdate.serviceId),
        username: accountToUpdate.username,
        password: accountToUpdate.password,
        observation: accountToUpdate.observation || '',
        profiles: updatedProfiles.map(profile => ({
          id: profile.id,
          name: profile.name,
          status: profile.status,
          clientId: profile.client_id || profile.clientId,
          pin: profile.pin,
          expiry_date: profile.expiry_date || profile.expiryDate,
          expiryDate: profile.expiryDate || profile.expiry_date
        }))
      };
      
      await axios.put(`${API_URL}/accounts/${editingProfile.accountId}`, accountData, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      
      await fetchAccounts();
      setShowEditProfileForm(false);
      setError(null);
      
    } catch (err) {
      console.error('Error:', err);
      setError('Error al editar el perfil. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountsFileUpload = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target.result);
          
          let accountsToImport = [];
          
          if (data.data && Array.isArray(data.data)) {
            accountsToImport = data.data;
          } else if (Array.isArray(data)) {
            accountsToImport = data;
          } else if (data.id && data.product && data.account_customer) {
            accountsToImport = [data];
          } else {
            throw new Error('Formato de datos no reconocido. Se espera un objeto o array de cuentas.');
          }
          
          setImportData(accountsToImport);
          setImportPreview(accountsToImport.slice(0, 1));
          setImportError('');
        } catch (error) {
          console.error('Error al procesar archivo JSON:', error);
          setImportError(`Error al procesar el archivo: ${error.message}`);
          setImportData(null);
          setImportPreview([]);
        }
      };
      
      reader.readAsText(file);
    }
  };

  // useAccounts.js - Versión mejorada de handleImportAccounts con mejor logging
  const handleImportAccounts = async (services, clients) => {
    if (!importData) return;
    
    try {
      setLoading(true);
      setImportError('');
      setImportSuccess('');
      
      if (!await ensureFreshCsrfToken()) {
        setImportError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      console.log('Iniciando importación de', importData.length, 'cuentas');
      
      // Obtener TODOS los clientes actuales de la base de datos
      const clientsResponse = await axios.get(`${API_URL}/clients`);
      const existingClients = clientsResponse.data;
      console.log('Clientes existentes en BD:', existingClients.length);
      
      // Función para normalizar nombres de servicios
      const normalizeServiceName = (name) => {
        return name
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '')  // Eliminar todos los espacios
          .replace(/[^a-z0-9]/g, '');  // Eliminar caracteres especiales
      };
      
      // Función para sanitizar nombres de perfiles
      const sanitizeProfileName = (name) => {
        if (!name) return 'Perfil';
        // Si el nombre es muy largo (como una URL), truncarlo
        if (name.length > 50) {
          return name.substring(0, 50);
        }
        // Remover caracteres problemáticos
        return name.replace(/[<>:"\/\\|?*]/g, '_');
      };
      
      const serviceAccountsMap = {};
      
      // Agrupar cuentas por servicio
      for (const account of importData) {
        const productName = account.product?.name || 'Servicio sin nombre';
        if (!serviceAccountsMap[productName]) {
          serviceAccountsMap[productName] = [];
        }
        serviceAccountsMap[productName].push(account);
      }
      
      const importedAccounts = [];
      const errorAccounts = [];
      const detailedErrors = [];
      
      for (const [productName, serviceAccounts] of Object.entries(serviceAccountsMap)) {
        try {
          let serviceId;
          
          // Mejorar la búsqueda del servicio con normalización
          console.log('Buscando servicio:', productName);
          
          const existingService = services.find(s => 
            normalizeServiceName(s.name) === normalizeServiceName(productName)
          );
          
          if (existingService) {
            serviceId = existingService.id;
            console.log('Servicio existente encontrado:', productName, 'ID:', serviceId);
          } else {
            console.log('Creando nuevo servicio:', productName);
            const sampleAccount = serviceAccounts[0];
            const serviceData = {
              name: productName,
              url: sampleAccount.product?.url_path || '',
              price: sampleAccount.product?.price || 0,
              maxProfiles: sampleAccount.product?.max_profiles || 1,
              image: sampleAccount.product?.image_url || 'https://via.placeholder.com/48',
              _csrf: csrfToken
            };
            
            const serviceResponse = await axios.post(`${API_URL}/services`, serviceData, {
              headers: {
                'X-CSRF-Token': csrfToken
              }
            });
            
            serviceId = serviceResponse.data.id;
            console.log('Nuevo servicio creado con ID:', serviceId);
          }
          
          for (const account of serviceAccounts) {
            try {
              // Validar datos requeridos
              if (!account.email) {
                console.error('Cuenta sin email:', account);
                throw new Error('Email es requerido');
              }
              
              console.log(`Procesando cuenta: ${account.email} (ID: ${account.id})`);
              
              const profiles = [];
              const maxProfiles = account.product?.max_profiles || 1;
              
              // Procesar perfiles con mejor manejo de valores null
              if (account.account_customer && Array.isArray(account.account_customer)) {
                console.log(`Procesando ${account.account_customer.length} perfiles para ${account.email}`);
                
                for (let i = 0; i < maxProfiles; i++) {
                  const customerProfile = account.account_customer[i];
                  
                  // Verificar si el perfil es válido (no es null y tiene customer)
                  if (customerProfile && customerProfile !== null && customerProfile.customer) {
                    console.log(`Perfil ${i + 1} tiene cliente: ${customerProfile.customer.name}`);
                    
                    let clientId = null;
                    
                    const customerName = customerProfile.customer.name;
                    const customerPhone = customerProfile.customer.cellphone || 
                                        customerProfile.customer.cellphone_formatted || '';
                    
                    if (customerName) {
                      // Normalizar el teléfono para la comparación
                      const normalizePhone = (phone) => {
                        if (!phone) return '';
                        return phone.replace(/[\s\-()+ ]/g, '');
                      };
                      
                      const normalizedNewPhone = normalizePhone(customerPhone);
                      
                      // Buscar en la lista de clientes existentes (de la BD)
                      const existingClient = existingClients.find(c => {
                        const normalizedExistingPhone = normalizePhone(c.phone);
                        return c.name.toLowerCase().trim() === customerName.toLowerCase().trim() && 
                               (normalizedExistingPhone === normalizedNewPhone || 
                                c.phone === customerPhone);
                      });
                      
                      if (existingClient) {
                        clientId = existingClient.id;
                        console.log('Cliente existente encontrado:', customerName, 'ID:', clientId);
                      } else {
                        console.log('Creando nuevo cliente:', customerName);
                        const clientData = {
                          name: customerName,
                          phone: customerPhone,
                          type: 'cliente',
                          _csrf: csrfToken
                        };
                        
                        const clientResponse = await axios.post(`${API_URL}/clients`, clientData, {
                          headers: {
                            'X-CSRF-Token': csrfToken
                          }
                        });
                        
                        clientId = clientResponse.data.id;
                        // Actualizar la lista local para futuras referencias
                        existingClients.push(clientResponse.data);
                      }
                    }
                    
                    // Validar y corregir fechas
                    let expiryDate = null;
                    if (customerProfile.end_date) {
                      try {
                        const endDate = new Date(customerProfile.end_date);
                        
                        // Solo verificar si la fecha es inválida (no si está en el pasado)
                        if (isNaN(endDate.getTime())) {
                          console.warn(`Fecha inválida para perfil ${i + 1}: ${customerProfile.end_date}. Usando fecha por defecto.`);
                          const fallbackDate = new Date();
                          fallbackDate.setDate(fallbackDate.getDate() + 30);
                          expiryDate = fallbackDate.toISOString().slice(0, 10);
                        } else {
                          // Usar la fecha tal como está, incluso si está en el pasado
                          expiryDate = endDate.toISOString().slice(0, 10);
                        }
                      } catch (dateError) {
                        console.error(`Error procesando fecha para perfil ${i + 1}:`, dateError);
                        const fallbackDate = new Date();
                        fallbackDate.setDate(fallbackDate.getDate() + 30);
                        expiryDate = fallbackDate.toISOString().slice(0, 10);
                      }
                    }
                    
                    // Sanitizar el nombre del perfil
                    const profileName = sanitizeProfileName(customerProfile.profile_name) || `Perfil ${i + 1}`;
                    
                    // Sanitizar el PIN (asegurar que solo contenga números)
                    let sanitizedPin = null;
                    if (customerProfile.pin) {
                      // Extraer solo números del PIN
                      const numericPin = customerProfile.pin.toString().replace(/\D/g, '');
                      if (numericPin.length > 0) {
                        sanitizedPin = numericPin;
                      }
                    }
                    
                    profiles.push({
                      name: profileName,
                      status: 'ocupado',
                      clientId: clientId,
                      pin: sanitizedPin,
                      expiryDate: expiryDate
                    });
                  } else {
                    // Perfil vacío o null
                    console.log(`Perfil ${i + 1} está vacío o es null`);
                    profiles.push({
                      name: `Perfil ${i + 1}`,
                      status: 'libre',
                      clientId: null,
                      pin: null,
                      expiryDate: null
                    });
                  }
                }
              } else {
                // Si no hay información de perfiles, crear perfiles vacíos
                console.log(`No hay información de perfiles para ${account.email}, creando ${maxProfiles} perfiles vacíos`);
                for (let i = 0; i < maxProfiles; i++) {
                  profiles.push({
                    name: `Perfil ${i + 1}`,
                    status: 'libre',
                    clientId: null,
                    pin: null,
                    expiryDate: null
                  });
                }
              }
              
              // Manejar perfiles con nombres duplicados
              const uniqueProfileNames = new Set();
              profiles.forEach((profile, index) => {
                // Si el nombre ya existe, agregar un sufijo
                if (uniqueProfileNames.has(profile.name)) {
                  let counter = 1;
                  let newName = `${profile.name}_${counter}`;
                  while (uniqueProfileNames.has(newName)) {
                    counter++;
                    newName = `${profile.name}_${counter}`;
                  }
                  console.log(`Renombrando perfil duplicado de "${profile.name}" a "${newName}"`);
                  profile.name = newName;
                }
                uniqueProfileNames.add(profile.name);
              });
              
              // IMPORTANTE: Mapeo correcto de campos
              const accountData = {
                serviceId: serviceId,
                username: account.email,
                password: account.service_password || '',
                observation: account.password ? account.password.trim() : '',
                profiles: profiles.map(profile => ({
                  name: profile.name,
                  status: profile.status,
                  clientId: profile.clientId,
                  pin: profile.pin,
                  expiryDate: profile.expiryDate
                })),
                _csrf: csrfToken
              };
              
              console.log(`Importando cuenta: ${account.email} con ${profiles.length} perfiles`);
              console.log('Datos de la cuenta:', JSON.stringify(accountData, null, 2));
              
              const accountResponse = await axios.post(`${API_URL}/accounts`, accountData, {
                headers: {
                  'X-CSRF-Token': csrfToken
                }
              });
              
              importedAccounts.push(accountResponse.data);
              console.log(`Cuenta importada exitosamente: ${account.email}`);
            } catch (err) {
              console.error('Error detallado al importar cuenta:', account.email, err);
              console.error('Datos enviados:', JSON.stringify(account, null, 2));
              console.error('Respuesta del servidor:', err.response?.data);
              console.error('Estado HTTP:', err.response?.status);
              console.error('Headers:', err.response?.headers);
              
              const errorDetail = {
                account: account.email || 'Sin email',
                accountId: account.id,
                error: err.response?.data?.error || err.message || 'Error desconocido',
                details: err.response?.data,
                status: err.response?.status,
                fullError: err,
                sentData: account
              };
              errorAccounts.push(errorDetail);
              detailedErrors.push(errorDetail);
            }
          }
        } catch (err) {
          console.error('Error al procesar servicio:', productName, err);
          serviceAccounts.forEach(account => {
            errorAccounts.push({
              account: account.email || 'Sin email',
              accountId: account.id,
              error: `Error al crear/obtener servicio: ${err.message}`,
              service: productName,
              fullError: err
            });
          });
        }
      }
      
      console.log('Resumen de importación:');
      console.log('- Total de cuentas a importar:', importData.length);
      console.log('- Cuentas importadas con éxito:', importedAccounts.length);
      console.log('- Cuentas con error:', errorAccounts.length);
      
      if (detailedErrors.length > 0) {
        console.log('Errores detallados:');
        detailedErrors.forEach(error => {
          console.log(`- Cuenta ${error.account} (ID: ${error.accountId}):`, error.error);
          console.log('  Estado HTTP:', error.status);
          if (error.details) {
            console.log('  Detalles:', error.details);
          }
          if (error.sentData) {
            console.log('  Datos enviados:', JSON.stringify(error.sentData, null, 2));
          }
        });
      }
      
      if (errorAccounts.length === 0) {
        setImportSuccess(`Se importaron ${importedAccounts.length} cuentas con éxito.`);
      } else {
        setImportSuccess(`Se importaron ${importedAccounts.length} cuentas, pero hubo errores en ${errorAccounts.length} cuentas. Revise la consola para más detalles.`);
      }
      
      await fetchAccounts();
      
      setTimeout(() => {
        setShowImportAccountsModal(false);
        setImportData(null);
        setImportPreview([]);
        setImportError('');
        setImportSuccess('');
      }, 3000);
      
    } catch (err) {
      console.error('Error general en la importación:', err);
      setImportError('Error en el proceso de importación. Intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProfile = () => {
    setNewAccount({
      ...newAccount,
      profiles: [...newAccount.profiles, { name: '', status: 'libre' }]
    });
  };

  const handleProfileChange = (index, field, value) => {
    const updatedProfiles = [...newAccount.profiles];
    updatedProfiles[index] = { ...updatedProfiles[index], [field]: value };
    setNewAccount({ ...newAccount, profiles: updatedProfiles });
  };

  const handleServiceChange = (serviceId, services) => {
    const service = services.find(s => s.id === parseInt(serviceId));
    
    if (service) {
      const maxProfiles = service.max_profiles || service.maxProfiles || 0;
      
      const profiles = Array.from({ length: maxProfiles }, (_, index) => ({
        name: `Perfil ${index + 1}`,
        status: 'libre'
      }));
      
      setNewAccount({
        ...newAccount,
        serviceId,
        profiles,
        observation: newAccount.observation
      });
    } else {
      setNewAccount({
        ...newAccount,
        serviceId,
        profiles: [{ name: '', status: 'libre' }],
        observation: newAccount.observation
      });
    }
  };

  const isProfileNearExpiry = (profile) => {
    if (!profile.expiryDate && !profile.expiry_date) return false;
    
    const expiryDate = new Date(profile.expiryDate || profile.expiry_date);
    const today = new Date();
    
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 && diffDays <= 5;
  };

  const isProfileExpired = (profile) => {
    if (!profile.expiryDate && !profile.expiry_date) return false;
    
    const expiryDate = new Date(profile.expiryDate || profile.expiry_date);
    const today = new Date();
    
    return expiryDate < today;
  };

  const filterClients = (query, clients) => {
    if (!query) {
      setFilteredClients([]);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    
    const filtered = clients.filter(client => {
      const nameMatch = client.name?.toLowerCase().includes(lowerQuery);
      const phoneMatch = client.phone?.toLowerCase().includes(lowerQuery);
      return nameMatch || phoneMatch;
    });
    
    setFilteredClients(filtered);
    setShowClientResults(true);
  };

  const handleClientSearch = (e, clients) => {
    const query = e.target.value;
    setClientSearchQuery(query);
    filterClients(query, clients);
  };

  const handleSelectClient = (client) => {
    setProfileAssignment({
      ...profileAssignment,
      clientId: client.id
    });
    setClientSearchQuery(`${client.name} (${client.phone || 'Sin teléfono'})`);
    setShowClientResults(false);
  };

  const getFilteredAccounts = (services) => {
    let filtered = accounts;
    
    // Solo aplicamos el filtrado de cuentas ocultas en el cliente
    if (!showHiddenAccounts) {
      filtered = filtered.filter(account => !hiddenAccounts.includes(account.id));
    }
    
    // Ordenamiento usando las funciones del cliente
    filtered.sort((a, b) => {
      const aPriority = a.profiles.some(p => isProfileExpired(p)) ? 3 : 
                       a.profiles.some(p => isProfileNearExpiry(p)) ? 2 : 1;
      
      const bPriority = b.profiles.some(p => isProfileExpired(p)) ? 3 : 
                       b.profiles.some(p => isProfileNearExpiry(p)) ? 2 : 1;
      
      return bPriority - aPriority;
    });
    
    return filtered;
  };

  const getClientName = (clientId, clients) => {
    if (!clientId) return 'N/A';
    const client = clients.find(c => c.id === parseInt(clientId));
    return client ? client.name : 'N/A';
  };

  const getPaginatedAccounts = (services) => {
    // Como los datos ya vienen paginados del servidor, 
    // solo necesitamos aplicar los filtros
    return getFilteredAccounts(services);
  };

  const getServiceName = (serviceId, services) => {
    if (!serviceId) return 'N/A';
    const service = services.find(s => s.id === parseInt(serviceId));
    return service ? service.name : 'N/A';
  };

  const showProfileTooltip = (e, profile, account, serviceName, clients) => {
    e.preventDefault();
    
    const clientName = profile.client_id || profile.clientId ? 
      getClientName(profile.client_id || profile.clientId, clients) : 'N/A';
    
    const content = (
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-3 max-w-xs">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-700 mb-2">
          <span className="font-bold text-white">{profile.name}</span>
        </div>
        {profile.status === 'ocupado' && (
          <>
            <div className="text-xs text-gray-300 flex flex-col gap-1">
              <div><span className="text-gray-500">Cliente:</span> {clientName}</div>
              {profile.pin && <div><span className="text-gray-500">PIN:</span> {profile.pin}</div>}
              {(profile.expiryDate || profile.expiry_date) && (
                <div>
                  <span className="text-gray-500">Vence:</span> {formatDateCorrectly(profile.expiryDate || profile.expiry_date)}
                </div>
              )}
            </div>
            <button 
              className="mt-2 text-xs bg-blue-600 hover:bg-blue-700 text-white py-1 px-2 rounded flex items-center gap-1"
              onClick={() => handleEditProfile(account, profile)}
            >
              Editar perfil
            </button>
          </>
        )}
        {profile.status === 'libre' && (
          <button 
            className="mt-2 text-xs bg-green-600 hover:bg-green-700 text-white py-1 px-2 rounded flex items-center gap-1"
            onClick={() => handleAssignProfile(account, profile)}
          >
            Asignar perfil
          </button>
        )}
      </div>
    );
    
    const rect = e.currentTarget.getBoundingClientRect();
    const position = {
      x: rect.left + window.scrollX,
      y: rect.bottom + window.scrollY + 5,
    };
    
    setTooltipInfo({
      show: true,
      content,
      position
    });
  };

  const hideProfileTooltip = () => {
    setTooltipInfo({ show: false, content: null, position: { x: 0, y: 0 } });
  };

  const toggleHideAccount = (accountId) => {
    setHiddenAccounts(prev => {
      let updated;
      if (prev.includes(accountId)) {
        updated = prev.filter(id => id !== accountId);
      } else {
        updated = [...prev, accountId];
      }
      
      localStorage.setItem('hiddenAccounts', JSON.stringify(updated));
      
      return updated;
    });
  };

  const copyProfileInfo = (services, getServiceTemplate) => {
    const account = accounts.find(a => a.id === editingProfile.accountId);
    const service = services.find(s => s.id === parseInt(account.service_id || account.serviceId));
    const replacements = {
      nombre_servicio: service.name || 'N/A',
      correo: account.username || 'N/A',
      contraseña: account.password || 'N/A',
      perfil: editingProfile.name || 'N/A',
      pin: editingProfile.pin || 'N/A',
      fecha_vencimiento: formatDateCorrectly(editingProfile.expiryDate)
    };
    const templateText = getServiceTemplate(service.id);
    const infoToCopy = safeReplaceTemplateVars(templateText, replacements);
  
    navigator.clipboard.writeText(infoToCopy)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      })
      .catch(err => {
        console.error('Error al copiar al portapapeles:', err);
        alert('La información no pudo ser copiada automáticamente. Por favor, cópiela manualmente.');
      });
  };

  const safeReplaceTemplateVars = (template, replacements) => {
    if (!template) return '';
    
    return template.replace(/\{\{([^{}]+)\}\}/g, (match, key) => {
      return replacements.hasOwnProperty(key) ? replacements[key] || '' : match;
    });
  };

  const handleNextPage = () => {
    if (currentAccountPage < paginationInfo.totalPages) {
      setCurrentAccountPage(currentAccountPage + 1);
      fetchAccounts(currentAccountPage + 1, itemsPerPage);
    }
  };
  
  const handlePrevPage = () => {
    if (currentAccountPage > 1) {
      setCurrentAccountPage(currentAccountPage - 1);
      fetchAccounts(currentAccountPage - 1, itemsPerPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentAccountPage(1);
    fetchAccounts(1, newItemsPerPage);
  };

  return {
    accounts,
    loading,
    error,
    setError,
    showAccountForm,
    setShowAccountForm,
    showImportAccountsModal,
    setShowImportAccountsModal,
    showAssignProfileForm,
    setShowAssignProfileForm,
    showEditProfileForm,
    setShowEditProfileForm,
    importData,
    setImportData,
    importPreview,
    setImportPreview,
    importError,
    setImportError,
    importSuccess,
    setImportSuccess,
    newAccount,
    setNewAccount,
    profileAssignment,
    setProfileAssignment,
    editingProfile,
    setEditingProfile,
    currentAccountPage,
    itemsPerPage,
    searchTerm,
    setSearchTerm,
    clientSearchQuery,
    setClientSearchQuery,
    filteredClients,
    setFilteredClients,
    showClientResults,
    setShowClientResults,
    copySuccess,
    setCopySuccess,
    tooltipInfo,
    setTooltipInfo,
    hiddenAccounts,
    showHiddenAccounts,
    setShowHiddenAccounts,
    paginationInfo,
    setPaginationInfo,
    serviceFilter,
    setServiceFilter,
    setCurrentAccountPage,
    expiryFilter,
    setExpiryFilter,
    fetchAccounts,
    handleSaveAccount,
    handleDeleteAccount,
    handleEditAccount,
    handleAssignProfile,
    handleEditProfile,
    handleSaveProfileAssignment,
    handleSaveEditedProfile,
    handleAccountsFileUpload,
    handleImportAccounts,
    handleAddProfile,
    handleProfileChange,
    handleServiceChange,
    isProfileNearExpiry,
    isProfileExpired,
    filterClients,
    handleClientSearch,
    handleSelectClient,
    getFilteredAccounts,
    getPaginatedAccounts,
    getServiceName,
    showProfileTooltip,
    hideProfileTooltip,
    getClientName,
    toggleHideAccount,
    copyProfileInfo,
    safeReplaceTemplateVars,
    handleNextPage,
    handlePrevPage,
    handleItemsPerPageChange
  };
};
